// Sugalarm — Cycle 133: the kiosk Log numpad as a slide-in overlay.
//
// The kiosk is a fixed Chromium appliance with no OS keyboard, so it keeps
// the custom <Numpad> — but instead of always sitting in the entry card
// (crowding the controls), the pad now slides in over the Today's-entries
// card only while a numeric field is being entered, then slides back out.
//
// This component is rendered by whichever component owns the active
// numeric field (LogScreen for Carbs / BG / Exercise; InsulinFlow for the
// guided insulin units / carbs) and PORTALED into a host element overlaid
// on the Today's-entries card — so the live value binding stays with the
// field owner while the pad visually covers the right panel. It reuses the
// existing <Numpad> verbatim (digit/decimal/backspace/value-write logic
// unchanged); this only changes the pad's visibility, position, and
// entry/exit animation.
//
// Hidden by default = translated off-screen to the right (NOT display:none)
// so it can slide; `prefers-reduced-motion` drops the transition. The host
// is overflow-clipped + pointer-events:none, so a closed (off-screen) pad
// never blocks the Today's-entries rows beneath it.

import { createPortal } from "react-dom";

import { Button } from "../../components/ui/Button";
import { Numpad } from "../../components/ui/Numpad";
import styles from "./KioskNumpadOverlay.module.css";

interface Props {
  open: boolean;
  /** Active-field label shown above the keys (e.g. "Carbs", "Insulin units"). */
  label: string;
  value: string;
  unit?: string;
  onChange: (v: string) => void;
  allowDecimal?: boolean;
  maxLength?: number;
  onDone: () => void;
  /** The overlay host element (a positioned, overflow-clipped div over the
   *  Today's-entries card). Null until mounted → renders nothing. */
  portalTarget: HTMLElement | null;
}

export function KioskNumpadOverlay({
  open,
  label,
  value,
  unit,
  onChange,
  allowDecimal = true,
  maxLength = 5,
  onDone,
  portalTarget,
}: Props) {
  if (!portalTarget) return null;
  return createPortal(
    <div className={`${styles.panel} ${open ? styles.open : ""}`}>
      <div className={styles.readout}>
        <span className={styles.readoutLabel}>{label}</span>
        <span className={styles.readoutValue}>
          <span className={value === "" ? styles.readoutPlaceholder : ""}>
            {value === "" ? "—" : value}
          </span>
          {unit ? <span className={styles.readoutUnit}>{unit}</span> : null}
        </span>
      </div>
      <div className={styles.keys}>
        <Numpad
          value={value}
          onChange={onChange}
          allowDecimal={allowDecimal}
          maxLength={maxLength}
          unit={unit}
          placeholder="—"
          hideDisplay
        />
      </div>
      <Button onClick={onDone} full>
        Done
      </Button>
    </div>,
    portalTarget,
  );
}
